/**
 * Site X-Ray — standalone domain intelligence tool powered by Ahrefs.
 *
 * Enter any domain → see 24 months of DR, referring domains, organic
 * traffic, and position-bucket history (top 3 / 4-10 / 11-20 / 21-50 / 51+).
 * Server caches each (domain, country, months) tuple for 28 days to keep
 * repeated lookups free. Live Ahrefs unit cost is logged and surfaced in
 * the Ahrefs spend badge at the top of this page + on /status.
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import {
  ScanSearch, Loader2, Globe2, Clock, TrendingUp, Link2, Users,
  Database, AlertCircle, Gauge, RefreshCw, FileText, DollarSign, TrendingDown,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, AreaChart, Area, BarChart, Bar,
  CartesianGrid, XAxis, YAxis, Tooltip, Legend, ReferenceLine,
} from "recharts";

const inputCls =
  "w-full rounded-xl border border-[#e2e8f0] bg-white px-3.5 py-2.5 text-sm text-[#1a202c] focus:border-[#4fd1c5] focus:ring-2 focus:ring-[#4fd1c5]/15 outline-none transition-all font-mono-tight";
const labelCls = "text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold";

const COUNTRIES = [
  { code: "", label: "Global" },
  { code: "GB", label: "UK" },
  { code: "US", label: "US" },
  { code: "IE", label: "Ireland" },
  { code: "AU", label: "Australia" },
  { code: "CA", label: "Canada" },
  { code: "DE", label: "Germany" },
  { code: "FR", label: "France" },
  { code: "ES", label: "Spain" },
  { code: "IT", label: "Italy" },
  { code: "NL", label: "Netherlands" },
];

// Compact month label — "Jan '25" style — for chart X axes
function fmtMonth(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("en-GB", { month: "short", year: "2-digit" }).replace(" ", " '");
}
function fmtNum(n) {
  if (n == null) return "—";
  if (Math.abs(n) >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (Math.abs(n) >= 1_000) return (n / 1_000).toFixed(1) + "k";
  return String(n);
}
// Monthly SEO traffic value is returned in USD; the user base is in the UK
// so we display £ with an approximate 0.79 conversion. Same fixed rate the
// rest of the app uses for the status page spend summary.
const USD_TO_GBP = 0.79;
function fmtGbp(usd) {
  if (usd == null) return "—";
  const gbp = usd * USD_TO_GBP;
  if (gbp >= 1_000_000) return "£" + (gbp / 1_000_000).toFixed(1) + "M";
  if (gbp >= 1_000) return "£" + (gbp / 1_000).toFixed(1) + "k";
  return "£" + Math.round(gbp).toLocaleString();
}

/** Latest-vs-earliest delta badge rendered in the KPI header cards. */
function DeltaChip({ latest, earliest, format = fmtNum }) {
  if (latest == null || earliest == null) return null;
  const diff = latest - earliest;
  if (!diff) return <span className="text-[11px] text-[#a0aec0] font-mono-tight">·</span>;
  const positive = diff > 0;
  const sign = positive ? "+" : "";
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-bold font-mono-tight px-1.5 py-0.5 rounded ${
        positive
          ? "bg-[#4fd1c5]/15 text-[#2c7a7b]"
          : "bg-[#fed7d7]/50 text-[#c53030]"
      }`}
    >
      {sign}{format(diff)}
    </span>
  );
}

function StatCard({ icon: Icon, label, value, delta, hint }) {
  return (
    <div className="card-soft p-4" data-testid={`xray-stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-[#a0aec0] font-bold">
          <Icon className="h-3.5 w-3.5" strokeWidth={2.2} />
          {label}
        </div>
        {delta}
      </div>
      <div className="mt-1 font-display text-2xl text-[#1a202c] font-mono-tight">{value}</div>
      {hint && <div className="mt-0.5 text-[10px] text-[#a0aec0]">{hint}</div>}
    </div>
  );
}

export default function SiteXRay() {
  const [target, setTarget] = useState("");
  const [country, setCountry] = useState("GB");
  const [months, setMonths] = useState(24);
  const [running, setRunning] = useState(false);
  const [data, setData] = useState(null);
  const [usage, setUsage] = useState(null);

  const refreshUsage = async () => {
    try {
      const { data } = await api.get("/tools/site-xray/usage");
      setUsage(data);
    } catch {}
  };
  useEffect(() => { refreshUsage(); }, []);

  const submit = async (e, { force = false } = {}) => {
    if (e?.preventDefault) e.preventDefault();
    if (!target.trim()) return;
    setRunning(true);
    setData(null);
    try {
      const { data: body } = await api.post("/tools/site-xray/trends", {
        target: target.trim(),
        country: country || null,
        months,
        force,
      });
      setData(body);
      const unitsNote = body.from_cache ? "cached · 0 units" : `${body.units_cost} units`;
      toast.success(`Site X-Ray complete · ${unitsNote}`);
      if (body.errors?.length) {
        toast.warning(`Partial result: ${body.errors.join("; ")}`, { duration: 7000 });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Site X-Ray failed");
    } finally {
      setRunning(false);
      refreshUsage();
    }
  };

  // Derive chart series + head/tail values for the KPI cards
  const points = data?.points || [];
  const first = points[0] || {};
  const last = points[points.length - 1] || {};
  // Stacked-area wants `top3`, `top4_10`, etc. coerced to 0 for display
  // when any null exists (recharts hides whole bars otherwise).
  const bucketSeries = useMemo(
    () =>
      points.map((p) => ({
        date: p.date,
        "Top 3": p.top3 || 0,
        "4–10": p.top4_10 || 0,
        "11–20": p.top11_20 || 0,
        "21–50": p.top21_50 || 0,
        "51+": p.top51_plus || 0,
      })),
    [points],
  );

  const resultTarget = data?.target;

  return (
    <div className="space-y-5 pt-2" data-testid="site-xray-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold">Tools / Site X-Ray</div>
          <h1 className="font-display text-4xl text-[#1a202c] flex items-center gap-3 mt-1">
            <ScanSearch className="h-7 w-7 text-[#4fd1c5]" strokeWidth={2} />
            Site X-Ray
          </h1>
          <p className="text-sm text-[#718096] mt-1 max-w-2xl">
            24 months of Ahrefs history on any domain — Domain Rating, referring domains,
            organic traffic, and keyword position buckets. Repeated lookups served from a 28-day cache.
          </p>
        </div>
        {usage && (
          <div
            data-testid="site-xray-usage-badge"
            className="inline-flex items-center gap-2 rounded-xl border border-[#e2e8f0] bg-white px-3.5 py-2 text-[11px] font-mono-tight text-[#4a5568]"
            title={`${usage.calls} Ahrefs calls this month · ${usage.units} units burned`}
          >
            <Gauge className="h-3.5 w-3.5" strokeWidth={2.2} />
            <span>
              Ahrefs <span className="font-semibold text-[#1a202c]">{usage.units.toLocaleString()}</span>
              <span className="opacity-70"> units · {usage.calls} calls · {usage.period}</span>
            </span>
          </div>
        )}
      </div>

      {usage && !usage.configured && (
        <div
          className="card-soft p-4 border border-[#feb2b2] bg-[#fef5e7]/60 flex items-start gap-3"
          data-testid="site-xray-no-key-banner"
        >
          <AlertCircle className="h-5 w-5 text-[#c05621] mt-0.5 shrink-0" />
          <div className="text-sm text-[#744210]">
            <div className="font-semibold">Ahrefs key not set.</div>
            <div className="text-[12px] mt-0.5">
              Add <code className="font-mono-tight bg-[#fff5eb] px-1 py-0.5 rounded">AHREFS_API_KEY</code> to <code className="font-mono-tight">backend/.env</code> and restart the backend. Create a key at
              <a className="text-[#2c7a7b] underline ml-1" href="https://app.ahrefs.com/account/api/profile" target="_blank" rel="noreferrer">app.ahrefs.com → Account → API</a>.
            </div>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="card-soft p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr_auto] gap-4 items-end">
          <div>
            <label className={labelCls} htmlFor="xray-target">Domain</label>
            <input
              id="xray-target"
              data-testid="site-xray-target"
              type="text"
              placeholder="example.com"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              required
              className={`${inputCls} mt-1.5`}
            />
          </div>
          <div>
            <label className={labelCls} htmlFor="xray-country">Country</label>
            <select
              id="xray-country"
              data-testid="site-xray-country"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className={`${inputCls} mt-1.5`}
            >
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>{c.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls} htmlFor="xray-months">Months</label>
            <select
              id="xray-months"
              data-testid="site-xray-months"
              value={months}
              onChange={(e) => setMonths(parseInt(e.target.value, 10))}
              className={`${inputCls} mt-1.5`}
            >
              <option value={6}>6</option>
              <option value={12}>12</option>
              <option value={24}>24</option>
              <option value={36}>36</option>
              <option value={60}>60</option>
            </select>
          </div>
          <button
            type="submit"
            disabled={running || !target.trim()}
            data-testid="site-xray-submit"
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ScanSearch className="h-3.5 w-3.5" />}
            {running ? "Scanning…" : "Scan"}
          </button>
        </div>
        <div className="text-[11px] text-[#a0aec0]">
          ~1,250 Ahrefs units per 24-month scan (first time; 5 endpoints). Repeat scans for the same combo are served from cache for 28 days.
        </div>
      </form>

      {data && (
        <div className="space-y-5" data-testid="site-xray-results">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2 text-sm">
              <Globe2 className="h-4 w-4 text-[#4fd1c5]" />
              <span className="font-display text-xl text-[#1a202c]">{resultTarget}</span>
              <span className="text-[#a0aec0]">·</span>
              <span className="font-mono-tight text-[11px] text-[#718096]">
                {data.country || "Global"} · {points.length} months
              </span>
              {data.from_cache ? (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-[#2c7a7b] bg-[#4fd1c5]/15 border border-[#4fd1c5]/40 px-2 py-0.5 rounded">
                  <Database className="h-3 w-3" /> cached
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-[#4a5568] bg-white border border-[#e2e8f0] px-2 py-0.5 rounded">
                  <Clock className="h-3 w-3" /> live · {data.units_cost} units
                </span>
              )}
              <button
                type="button"
                onClick={() => submit(null, { force: true })}
                disabled={running}
                data-testid="site-xray-force-refresh"
                title="Bypass the 28-day cache and pull fresh from Ahrefs (burns units)"
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-[#718096] hover:text-[#2c7a7b] bg-white border border-[#e2e8f0] hover:border-[#4fd1c5] px-2 py-0.5 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className={`h-3 w-3 ${running ? "animate-spin" : ""}`} /> Force refresh
              </button>
            </div>
          </div>

          {/* KPI cards — current value + delta over the full window */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <StatCard
              icon={TrendingUp}
              label="Domain Rating"
              value={last.domain_rating?.toFixed?.(1) ?? "—"}
              hint={`from ${first.domain_rating?.toFixed?.(1) ?? "—"}`}
              delta={<DeltaChip latest={last.domain_rating} earliest={first.domain_rating} format={(n) => n.toFixed(1)} />}
            />
            <StatCard
              icon={Link2}
              label="Referring domains"
              value={fmtNum(last.refdomains)}
              hint={`from ${fmtNum(first.refdomains)}`}
              delta={<DeltaChip latest={last.refdomains} earliest={first.refdomains} />}
            />
            <StatCard
              icon={Users}
              label="Organic traffic"
              value={`${fmtNum(last.organic_traffic)}/mo`}
              hint={`from ${fmtNum(first.organic_traffic)}/mo`}
              delta={<DeltaChip latest={last.organic_traffic} earliest={first.organic_traffic} />}
            />
            <StatCard
              icon={DollarSign}
              label="SEO value"
              value={`${fmtGbp(last.organic_value)}/mo`}
              hint={`from ${fmtGbp(first.organic_value)}/mo`}
              delta={<DeltaChip latest={last.organic_value} earliest={first.organic_value} format={fmtGbp} />}
            />
            <StatCard
              icon={FileText}
              label="Organic pages"
              value={fmtNum(last.organic_pages)}
              hint={`from ${fmtNum(first.organic_pages)}`}
              delta={<DeltaChip latest={last.organic_pages} earliest={first.organic_pages} />}
            />
            <StatCard
              icon={Gauge}
              label="Top-10 rankings"
              value={fmtNum(last.top10_cumulative)}
              hint={`from ${fmtNum(first.top10_cumulative)}`}
              delta={<DeltaChip latest={last.top10_cumulative} earliest={first.top10_cumulative} />}
            />
          </div>

          {/* Chart 1 — DR + organic traffic (dual axis) */}
          <div className="card-soft p-5" data-testid="xray-chart-authority">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="h-4 w-4 text-[#4fd1c5]" />
              <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-bold">Authority & Organic Traffic</div>
            </div>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={points} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#edf2f7" />
                <XAxis dataKey="date" tickFormatter={fmtMonth} tick={{ fontSize: 10, fill: "#718096" }} />
                <YAxis yAxisId="dr" tick={{ fontSize: 10, fill: "#4fd1c5" }} domain={[0, 100]} width={32} />
                <YAxis yAxisId="traffic" orientation="right" tick={{ fontSize: 10, fill: "#dd6b20" }} tickFormatter={fmtNum} width={44} />
                <Tooltip
                  labelFormatter={fmtMonth}
                  contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #e2e8f0" }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line yAxisId="dr" type="monotone" dataKey="domain_rating" name="Domain Rating" stroke="#4fd1c5" strokeWidth={2} dot={false} />
                <Line yAxisId="traffic" type="monotone" dataKey="organic_traffic" name="Est. monthly traffic" stroke="#dd6b20" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Chart 2 — Referring domains (cumulative) */}
          <div className="card-soft p-5" data-testid="xray-chart-refdomains">
            <div className="flex items-center gap-2 mb-3">
              <Link2 className="h-4 w-4 text-[#805ad5]" />
              <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-bold">Referring Domains (cumulative)</div>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={points} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="rdGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#805ad5" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#805ad5" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#edf2f7" />
                <XAxis dataKey="date" tickFormatter={fmtMonth} tick={{ fontSize: 10, fill: "#718096" }} />
                <YAxis tickFormatter={fmtNum} tick={{ fontSize: 10, fill: "#718096" }} width={44} />
                <Tooltip labelFormatter={fmtMonth} contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #e2e8f0" }} />
                <Area type="monotone" dataKey="refdomains" name="Referring domains" stroke="#805ad5" strokeWidth={2} fill="url(#rdGradient)" />
              </AreaChart>
            </ResponsiveContainer>
            <div className="text-[10px] text-[#a0aec0] mt-2">
              Ahrefs returns cumulative totals, not month-over-month gains. A flat line can hide equal gains + losses.
            </div>
          </div>

          {/* Chart 2b — Net new refdomains (derived from the cumulative series).
              This is the closest we can get to Ahrefs' "New & Lost" view without
              a dedicated endpoint — honest caveat shown underneath. */}
          <div className="card-soft p-5" data-testid="xray-chart-netrefs">
            <div className="flex items-center gap-2 mb-3">
              <TrendingDown className="h-4 w-4 text-[#dd6b20]" />
              <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-bold">Net new referring domains (month-over-month)</div>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={points} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#edf2f7" />
                <XAxis dataKey="date" tickFormatter={fmtMonth} tick={{ fontSize: 10, fill: "#718096" }} />
                <YAxis tickFormatter={fmtNum} tick={{ fontSize: 10, fill: "#718096" }} width={44} />
                <ReferenceLine y={0} stroke="#cbd5e0" />
                <Tooltip
                  labelFormatter={fmtMonth}
                  formatter={(v) => [v > 0 ? `+${v}` : v, "Net change"]}
                  contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #e2e8f0" }}
                />
                <Bar
                  dataKey="refdomains_net_new"
                  name="Net change"
                  // Positive bars = green, negative = red. Recharts doesn't
                  // support cell-level colouring on BarChart without <Cell>,
                  // so we rely on a single colour and let the reference line
                  // visually separate positives/negatives. Simpler, cleaner.
                  fill="#38b2ac"
                />
              </BarChart>
            </ResponsiveContainer>
            <div className="text-[10px] text-[#a0aec0] mt-2">
              Derived from the cumulative series — a flat bar can hide churn (10 new refdomains + 10 lost = net 0). Ahrefs API doesn't expose per-month new/lost counts directly.
            </div>
          </div>

          {/* Chart 3 — SEO traffic value */}
          <div className="card-soft p-5" data-testid="xray-chart-value">
            <div className="flex items-center gap-2 mb-3">
              <DollarSign className="h-4 w-4 text-[#38a169]" />
              <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-bold">SEO traffic value (£/month)</div>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={points} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="valueGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#38a169" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#38a169" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#edf2f7" />
                <XAxis dataKey="date" tickFormatter={fmtMonth} tick={{ fontSize: 10, fill: "#718096" }} />
                <YAxis
                  tickFormatter={(v) => fmtGbp(v)}
                  tick={{ fontSize: 10, fill: "#718096" }}
                  width={60}
                />
                <Tooltip
                  labelFormatter={fmtMonth}
                  formatter={(v) => [fmtGbp(v), "Monthly SEO value"]}
                  contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #e2e8f0" }}
                />
                <Area type="monotone" dataKey="organic_value" name="SEO traffic value" stroke="#38a169" strokeWidth={2} fill="url(#valueGradient)" />
              </AreaChart>
            </ResponsiveContainer>
            <div className="text-[10px] text-[#a0aec0] mt-2">
              What the organic clicks would cost to buy on Google Ads this month. Ahrefs returns USD — converted here at $0.79/£.
            </div>
          </div>

          {/* Chart 4 — Organic pages count */}
          <div className="card-soft p-5" data-testid="xray-chart-pages">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="h-4 w-4 text-[#3182ce]" />
              <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-bold">Organic pages (number of URLs ranking)</div>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={points} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="pagesGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3182ce" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#3182ce" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#edf2f7" />
                <XAxis dataKey="date" tickFormatter={fmtMonth} tick={{ fontSize: 10, fill: "#718096" }} />
                <YAxis tickFormatter={fmtNum} tick={{ fontSize: 10, fill: "#718096" }} width={44} />
                <Tooltip
                  labelFormatter={fmtMonth}
                  formatter={(v) => [fmtNum(v), "Pages ranking"]}
                  contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #e2e8f0" }}
                />
                <Area type="monotone" dataKey="organic_pages" name="Organic pages" stroke="#3182ce" strokeWidth={2} fill="url(#pagesGradient)" />
              </AreaChart>
            </ResponsiveContainer>
            <div className="text-[10px] text-[#a0aec0] mt-2">
              How many URLs on the domain rank somewhere in Google's organic results. Rising = content investment paying off; sudden drop = indexation/penalty signal.
            </div>
          </div>

          {/* Chart 5 — Position buckets stacked */}
          <div className="card-soft p-5" data-testid="xray-chart-buckets">
            <div className="flex items-center gap-2 mb-3">
              <Gauge className="h-4 w-4 text-[#38b2ac]" />
              <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-bold">Organic positions by bucket</div>
            </div>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={bucketSeries} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#edf2f7" />
                <XAxis dataKey="date" tickFormatter={fmtMonth} tick={{ fontSize: 10, fill: "#718096" }} />
                <YAxis tickFormatter={fmtNum} tick={{ fontSize: 10, fill: "#718096" }} width={44} />
                <Tooltip labelFormatter={fmtMonth} contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #e2e8f0" }} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Bar dataKey="Top 3" stackId="a" fill="#2c7a7b" />
                <Bar dataKey="4–10" stackId="a" fill="#4fd1c5" />
                <Bar dataKey="11–20" stackId="a" fill="#9ae6b4" />
                <Bar dataKey="21–50" stackId="a" fill="#faf089" />
                <Bar dataKey="51+" stackId="a" fill="#fbb6a4" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}
